import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import type { CompanyInvoiceMode, WeeklyInvoiceDay } from 'src/constants/distributor-invoicing';
import {
  Company,
  CreateCompanyPayload,
  EditCompanyPayload,
} from 'src/interfaces/company';
import { CompanySearchDialogComponent } from '../components/company-search-dialog/company-search-dialog.component';
import { AlertService } from './alert.service';
import { AuthService } from './auth.service';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  baseUrl: string = environment.api.baseUrl;
  headers: HttpHeaders;
  totalNumberOfCompanies: number = 0;
  constructor(
    private _auth: AuthService,
    private _http: HttpClient,
    private _utilsService: UtilsService,
    private _alertService: AlertService,
    private _dialog: MatDialog
  ) {
    this._auth.generateRequestHeaders().then((headers) => {
      this.headers = headers;
    });
  }

  async createCompany(payload: CreateCompanyPayload): Promise<Company | null> {
    try {
      const { data, message } = await this._http
        .post<any>(`${this.baseUrl}/company`, payload, {
          headers: this.headers,
        })
        .toPromise();
      this._alertService.infoAlert(message);
      return data;
    } catch (error) {
      this._utilsService.showError(error);
      return null;
    }
  }

  async editCompany(id: number, payload: EditCompanyPayload): Promise<boolean> {
    try {
      const { success, message } = await this._http
        .put<any>(`${this.baseUrl}/company/${id}`, payload, {
          headers: this.headers,
        })
        .toPromise();
      this._alertService.infoAlert(message);
      return success;
    } catch (error) {
      this._utilsService.showError(error);
      return false;
    }
  }

  async getCompanies(
    page: number = 1,
    limit: number = 1,
    search: string = '',
    types: string | null = null,
    sort: string = 'asc',
    orderBy: string = 'name'
  ): Promise<Company[]> {
    let queryString: string = `?page=${page}&limit=${limit}&search=${search}`;

    if (types) {
      queryString += `&types=${types}`;
    }
    if (sort) {
      queryString += `&order_direction=${sort}`;
    }
    if (orderBy) {
      queryString += `&order_by=${orderBy}`;
    }
    try {
      const { data } = await this._http
        .get<any>(`${this.baseUrl}/company${queryString}`, {
          headers: this.headers,
        })
        .toPromise();
      this.totalNumberOfCompanies = data.total;
      return (data.results ?? []).map((c: any) => this.normalizeCompany(c));
    } catch (error) {
      this._utilsService.showError(error);
      return [];
    }
  }

  getTotalNumberOfCompanies() {
    return this.totalNumberOfCompanies;
  }

  /** Map API snake/camel variants so UI (e.g. Sage status) stays consistent after GET. */
  normalizeCompany(raw: any): Company {
    const sid =
      raw.sage_contact_id ?? raw.sageContactId ?? raw.sage_contactId ?? null;
    const invoice_mode =
      raw.invoice_mode ?? raw.invoiceMode ?? null;
    const weekly_invoice_day =
      raw.weekly_invoice_day ?? raw.weeklyInvoiceDay ?? null;
    const is_active =
      raw.is_active ?? raw.isActive ?? raw.active ?? null;
    return {
      ...raw,
      sage_contact_id:
        sid != null && String(sid).trim() !== ''
          ? String(sid)
          : raw.sage_contact_id ?? null,
      invoice_mode,
      weekly_invoice_day,
      is_active,
    } as Company;
  }

  async getCompany(id: number): Promise<Company | null> {
    try {
      const { data } = await this._http
        .get<any>(`${this.baseUrl}/company/${id}`, { headers: this.headers })
        .toPromise();
      if (data == null) return null;
      return this.normalizeCompany(data);
    } catch (error) {
      this._utilsService.showError(error);
      return null;
    }
  }

  openCompanySearchDialog(
    title: string = 'Search for a company',
    message: string = '',
    types: string | null = null
  ): Promise<Company | null> {
    const dialogRef = this._dialog.open(CompanySearchDialogComponent, {
      width: '900px',
      data: {
        title,
        message,
        types,
      },
    });

    return dialogRef.afterClosed().toPromise();
  }

  constructionCompanyEditpayload(
    company: Company,
    distributor: Company | null
  ): EditCompanyPayload {
    let distributor_id: number | undefined = undefined;

    if (distributor) distributor_id = distributor.id;
    else
      distributor_id = company.distributor_id
        ? company.distributor_id
        : undefined;
    return {
      id: company.id,
      name: company.name,
      email: company.email,
      email_alt: company.email_alt,
      phone_number: company.phone_number,
      phone_number_alt: company.phone_number_alt,
      practice_number: company.practice_number,
      vat_registration_number: company.vat_registration_number,
      company_type_id: company.company_type_id,
      primary_contact_id: company.primary_contact_id
        ? company.primary_contact_id
        : undefined,
      address: undefined,
      distributor_id,
      invoice_mode: (company.invoice_mode ?? null) as CompanyInvoiceMode | null,
      weekly_invoice_day: (company.weekly_invoice_day ?? null) as WeeklyInvoiceDay | null,
      is_active: company.is_active ?? null,
    };
  }
}
